import { useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { novaPoshtaReference, type CitySearchResult } from '../../../services/novaPoshtaClient';

interface NovaPoshtaCitySelectProps {
  apiKey: string | null;
  value: CitySearchResult | null;
  onChange: (value: CitySearchResult | null) => void;
  label: string;
  disabled?: boolean;
}

const DEBOUNCE_MS = 500;
const MIN_QUERY_LENGTH = 2;

// Lightweight, uncached direct-to-NP search (doc/shipping/plan.md Фаза 2 — the proper
// cached/BE-proxied version is Фаза 3). Debounced because Nova Poshta's rate limit is
// tight enough to trip on two rapid calls.
export default function NovaPoshtaCitySelect({
  apiKey,
  value,
  onChange,
  label,
  disabled,
}: NovaPoshtaCitySelectProps) {
  const [inputValue, setInputValue] = useState(value?.present ?? '');
  const [options, setOptions] = useState<CitySearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!apiKey || inputValue.length < MIN_QUERY_LENGTH || inputValue === value?.present) {
      return;
    }

    let cancelled = false;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const found = await novaPoshtaReference.searchSettlements(apiKey, inputValue);
        if (!cancelled) {
          setOptions(found);
        }
      } catch (err) {
        console.error('Nova Poshta settlement search failed:', err);
        if (!cancelled) {
          setError('Не вдалося виконати пошук');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
  }, [apiKey, inputValue, value]);

  return (
    <Autocomplete
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={(option) => option.present}
      isOptionEqualToValue={(option, val) => option.ref === val.ref}
      filterOptions={(x) => x}
      loading={loading}
      disabled={disabled || !apiKey}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          fullWidth
          error={!!error}
          helperText={error}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
